import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import '../../styles/RichTextEditor.css';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import { 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Code,
  X
} from 'lucide-react';

// Register custom Image blot that preserves class and style attributes (for resizing)
const QuillLib = ReactQuill.Quill;
if (QuillLib) {
  const BaseImageBlot = QuillLib.import('formats/image');
  class StyledImage extends BaseImageBlot {
    static create(value) {
      const src = typeof value === 'string' ? value : value?.src || '';
      const node = super.create(src);
      if (typeof value === 'object' && value !== null) {
        if (value.class) node.setAttribute('class', value.class);
        if (value.style) node.setAttribute('style', value.style);
      }
      return node;
    }
    static value(domNode) {
      const src = domNode.getAttribute('src');
      const cls = domNode.getAttribute('class');
      const style = domNode.getAttribute('style');
      if (!cls && !style) return src;
      const result = { src };
      if (cls) result.class = cls;
      if (style) result.style = style;
      return result;
    }
  }
  StyledImage.blotName = 'image';
  StyledImage.tagName = 'IMG';
  QuillLib.register(StyledImage, true);
}

// Cloudinary cloud name for URL transformation
const CLOUDINARY_CLOUD_NAME = 'dh9jfogh1';

// Max file size for Cloudinary (20MB)
const MAX_FILE_SIZE = 20 * 1024 * 1024;

// Compress image if too large
const compressImage = (file, maxSizeMB = 2) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions - max 2000px on longest side
        const maxDim = 2000;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with quality reduction
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              console.log(`🗜️ Compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          0.8 // 80% quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// Normalize spaces: convert &nbsp; to regular spaces for better cross-platform display
export const normalizeSpaces = (html) => {
  if (!html) return html;
  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/  +/g, ' ');
};

// Apply t_800 transformation to Cloudinary URLs for proper display
const applyCloudinaryTransformation = (url) => {
  if (!url || !url.includes('cloudinary.com') || !url.includes('/upload/')) {
    return url;
  }
  // Check if transformation already applied
  if (url.includes('/t_800/') || url.includes('/t_')) {
    return url;
  }
  return url.replace('/upload/', '/upload/t_800/');
};

// Base formats that are always available
const BASE_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'indent',
  'link'
];

// Extended formats including image (class attribute is handled by Quill's default image format)
const IMAGE_FORMATS = [...BASE_FORMATS, 'image', 'align'];

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  className,
  minHeight = '120px',
  enableImageUpload = false,
  imageFolder = 'submission_content'
}) => {
  const quillRef = useRef(null);
  const containerRef = useRef(null);
  const isInternalChange = useRef(false);
  const lastValue = useRef(value);
  const pendingImageInsert = useRef(false);
  
  // HTML source view state
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [htmlSourceValue, setHtmlSourceValue] = useState('');

  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkSavedRange, setLinkSavedRange] = useState(null);
  const [linkSelectedText, setLinkSelectedText] = useState('');
  const linkUrlInputRef = useRef(null);

  // Image toolbar state
  const [selectedImage, setSelectedImage] = useState(null);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showImageToolbar, setShowImageToolbar] = useState(false);
  const [imageClassKey, setImageClassKey] = useState(0); // Force re-render when classes change

  // Image resize state
  const [imageWidth, setImageWidth] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const resizeRef = useRef({ active: false, startX: 0, startWidth: 0, editorWidth: 0 });

  // Get current image classes
  const getImageClasses = (img) => {
    const classes = img?.className?.split(' ') || [];
    return {
      size: classes.find(c => ['img-small', 'img-medium', 'img-large', 'img-full'].includes(c)) || '',
      align: classes.find(c => ['img-left', 'img-center', 'img-right'].includes(c)) || ''
    };
  };

  // Apply class to image and trigger onChange
  const applyImageClass = (classType, className) => {
    console.log('🎨 applyImageClass called:', classType, className);
    if (!selectedImage) {
      console.log('❌ No selected image');
      return;
    }
    
    console.log('📸 Selected image:', selectedImage);
    const currentClasses = selectedImage.className?.split(' ').filter(c => c) || [];
    console.log('📋 Current classes:', currentClasses);
    
    // Remove existing classes of the same type
    const classesToRemove = classType === 'size' 
      ? ['img-small', 'img-medium', 'img-large', 'img-full']
      : ['img-left', 'img-center', 'img-right'];
    
    const filteredClasses = currentClasses.filter(c => !classesToRemove.includes(c));
    
    // Add the new class if it's not empty (toggle off if same class clicked)
    if (className && !currentClasses.includes(className)) {
      filteredClasses.push(className);
    }
    
    selectedImage.className = filteredClasses.join(' ');
    console.log('✅ New classes applied:', selectedImage.className);
    
    // Trigger onChange with updated HTML
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const content = quill.root.innerHTML;
      isInternalChange.current = true;
      lastValue.current = content;
      onChange(normalizeSpaces(content));
      console.log('💾 Content saved');
    }
    
    // Force re-render of toolbar to show updated state
    setImageClassKey(prev => prev + 1);
  };

  // Get current width of selected image as a percentage of editor width
  const getImageWidthPercent = useCallback((img) => {
    if (!img) return 100;
    if (img.style.width && img.style.width.endsWith('%')) {
      return parseInt(img.style.width);
    }
    const classes = img.className?.split(' ') || [];
    if (classes.includes('img-small')) return 25;
    if (classes.includes('img-medium')) return 50;
    if (classes.includes('img-large')) return 75;
    if (classes.includes('img-full')) return 100;
    const quill = quillRef.current?.getEditor();
    if (!quill) return 100;
    const editorWidth = quill.root.clientWidth - 24;
    if (editorWidth <= 0) return 100;
    return Math.min(100, Math.round((img.clientWidth / editorWidth) * 100));
  }, []);

  // Apply a custom width percentage to the selected image
  const applyCustomWidth = useCallback((widthPercent) => {
    if (!selectedImage) return;
    const clamped = Math.max(10, Math.min(100, Math.round(widthPercent)));
    // Remove size classes since we're using inline width now
    const currentClasses = selectedImage.className?.split(' ').filter(c => c) || [];
    const filteredClasses = currentClasses.filter(c =>
      !['img-small', 'img-medium', 'img-large', 'img-full'].includes(c)
    );
    selectedImage.className = filteredClasses.join(' ');
    selectedImage.style.width = `${clamped}%`;
    selectedImage.style.height = 'auto';
    setImageWidth(clamped);
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const content = quill.root.innerHTML;
      isInternalChange.current = true;
      lastValue.current = content;
      onChange(normalizeSpaces(content));
    }
    setImageClassKey(prev => prev + 1);
  }, [selectedImage, onChange]);

  // Sync imageWidth state when an image is selected
  useEffect(() => {
    if (selectedImage) {
      setImageWidth(getImageWidthPercent(selectedImage));
    }
  }, [selectedImage, imageClassKey, getImageWidthPercent]);

  // Resize drag: start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImage) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    resizeRef.current = {
      active: true,
      startX: e.clientX,
      startWidth: selectedImage.clientWidth,
      editorWidth: quill.root.clientWidth - 24
    };
    setIsDragging(true);
  }, [selectedImage]);

  // Resize drag: move + end listeners
  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      if (!resizeRef.current.active || !selectedImage) return;
      const { startX, startWidth, editorWidth } = resizeRef.current;
      const deltaX = e.clientX - startX;
      const newWidth = startWidth + deltaX;
      const newPercent = Math.round((newWidth / editorWidth) * 100);
      const clamped = Math.max(10, Math.min(100, newPercent));
      selectedImage.style.width = `${clamped}%`;
      selectedImage.style.height = 'auto';
      setImageWidth(clamped);
    };
    const handleMouseUp = () => {
      resizeRef.current.active = false;
      setIsDragging(false);
      if (selectedImage) {
        const currentClasses = selectedImage.className?.split(' ').filter(c => c) || [];
        const filteredClasses = currentClasses.filter(c =>
          !['img-small', 'img-medium', 'img-large', 'img-full'].includes(c)
        );
        selectedImage.className = filteredClasses.join(' ');
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const content = quill.root.innerHTML;
          isInternalChange.current = true;
          lastValue.current = content;
          onChange(normalizeSpaces(content));
        }
        setImageClassKey(prev => prev + 1);
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, selectedImage, onChange]);

  // Compute resize handle overlay position (viewport coords for fixed positioning)
  const getOverlayRect = useCallback(() => {
    if (!selectedImage) return null;
    const imgRect = selectedImage.getBoundingClientRect();
    return {
      top: imgRect.top,
      left: imgRect.left,
      width: imgRect.width,
      height: imgRect.height
    };
  }, [selectedImage]);

  // Close toolbar on scroll so it doesn't float in wrong position
  useEffect(() => {
    if (!showImageToolbar) return;
    const handleScroll = () => {
      setShowImageToolbar(false);
      setSelectedImage(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showImageToolbar]);

  // Close toolbar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showImageToolbar && !e.target.closest('.image-style-toolbar') && e.target.tagName !== 'IMG') {
        setShowImageToolbar(false);
        setSelectedImage(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showImageToolbar]);

  // Attach click handler directly to Quill editor content area
  useEffect(() => {
    if (!enableImageUpload) return;
    
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const editorRoot = quill.root;
    
    const handleImageClick = (e) => {
      console.log('🖼️ Quill editor clicked, target:', e.target.tagName);
      
      if (e.target.tagName === 'IMG') {
        console.log('✅ Image clicked!', e.target.src?.substring(0, 50));
        const img = e.target;
        const containerRect = containerRef.current?.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        
        console.log('📍 Container rect:', containerRect);
        console.log('📍 Image rect:', imgRect);
        
        if (containerRect) {
          // Use viewport coordinates for fixed positioning (avoids overflow:hidden clipping)
          const toolbarWidth = 320;
          const toolbarHeight = 80;
          
          // Try above image first, then below if not enough space
          let top = imgRect.top - toolbarHeight - 8;
          if (top < 10) {
            top = imgRect.bottom + 8;
          }
          // Keep within viewport vertically
          top = Math.max(10, Math.min(top, window.innerHeight - toolbarHeight - 10));
          
          // Center horizontally on the image, but keep within viewport
          let left = imgRect.left + (imgRect.width / 2) - (toolbarWidth / 2);
          left = Math.max(10, Math.min(left, window.innerWidth - toolbarWidth - 10));
          
          console.log('📍 Toolbar position (fixed):', { top, left });
          setToolbarPosition({ top, left });
        }
        
        setSelectedImage(img);
        setShowImageToolbar(true);
        console.log('🔧 Toolbar should now show');
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    editorRoot.addEventListener('click', handleImageClick);
    return () => editorRoot.removeEventListener('click', handleImageClick);
  }, [enableImageUpload]);

  // Toggle HTML source view
  const toggleHtmlSource = useCallback(() => {
    if (showHtmlSource) {
      // Switching from HTML source back to WYSIWYG — apply the edited HTML
      isInternalChange.current = true;
      lastValue.current = htmlSourceValue;
      onChange(htmlSourceValue);
      setShowHtmlSource(false);
    } else {
      // Switching to HTML source — populate textarea with current value
      setHtmlSourceValue(value || '');
      setShowHtmlSource(true);
    }
  }, [showHtmlSource, htmlSourceValue, value, onChange]);

  // Custom link handler — opens a modal for URL + display text
  const linkHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    if (!range) return;

    // Check if there's already a link at cursor
    const [leaf] = quill.getLeaf(range.index);
    const existingLink = leaf?.parent?.domNode?.tagName === 'A' ? leaf.parent.domNode.getAttribute('href') : null;

    const selectedText = range.length > 0 ? quill.getText(range.index, range.length) : '';

    setLinkSavedRange(range);
    setLinkSelectedText(selectedText);
    setLinkText(selectedText);
    setLinkUrl(existingLink || '');
    setShowLinkModal(true);

    // Focus the URL input after modal opens
    setTimeout(() => linkUrlInputRef.current?.focus(), 100);
  }, []);

  // Apply the link from the modal
  const applyLink = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill || !linkSavedRange) return;

    const url = linkUrl.trim();

    if (!url) {
      // Remove link if URL is empty
      if (linkSavedRange.length > 0) {
        quill.formatText(linkSavedRange.index, linkSavedRange.length, 'link', false);
      }
      setShowLinkModal(false);
      return;
    }

    const displayText = linkText.trim() || url;

    if (linkSavedRange.length > 0) {
      // There was selected text — replace it with new text + link
      quill.deleteText(linkSavedRange.index, linkSavedRange.length);
      quill.insertText(linkSavedRange.index, displayText, 'link', url);
    } else {
      // No selection — insert new text with link
      quill.insertText(linkSavedRange.index, displayText, 'link', url);
    }

    // Trigger change
    const content = quill.root.innerHTML;
    isInternalChange.current = true;
    lastValue.current = content;
    onChange(normalizeSpaces(content));

    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    setLinkSavedRange(null);
  }, [linkUrl, linkText, linkSavedRange, onChange]);

  // Custom image handler for Cloudinary upload
  const imageHandler = useCallback(() => {
    // Create and trigger file input
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      let file = input.files[0];
      if (!file) return;

      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      // Save cursor position
      const range = quill.getSelection(true);

      // Check file size and compress if needed
      if (file.size > MAX_FILE_SIZE) {
        console.log(`📦 File too large (${(file.size / 1024 / 1024).toFixed(2)}MB), compressing...`);
        quill.insertText(range.index, 'Compressing image...', { italic: true });
        quill.setSelection(range.index + 20);
        
        try {
          file = await compressImage(file);
          quill.deleteText(range.index, 20);
        } catch (compressError) {
          console.error('Compression failed:', compressError);
          quill.deleteText(range.index, 20);
          alert('Image is too large (max 20MB) and compression failed. Please use a smaller image.');
          return;
        }
        
        // Check if still too large after compression
        if (file.size > MAX_FILE_SIZE) {
          alert('Image is still too large after compression. Please use a smaller image.');
          return;
        }
      }

      // Show uploading placeholder
      quill.insertText(range.index, 'Uploading image...', { italic: true });
      quill.setSelection(range.index + 18);

      try {
        // Upload to Cloudinary
        const result = await uploadToCloudinary(file, 'image', imageFolder);

        // Apply t_800 transformation for proper display
        const transformedUrl = applyCloudinaryTransformation(result.url);

        // Remove placeholder text
        quill.deleteText(range.index, 18);

        // Mark that we're about to insert an image (so onChange knows to process it)
        pendingImageInsert.current = true;

        // Insert the image
        quill.insertEmbed(range.index, 'image', transformedUrl);

        // Move cursor after image
        quill.setSelection(range.index + 1);

        // Manually trigger change since insertEmbed uses 'api' source
        const content = quill.root.innerHTML;
        console.log('Image inserted, triggering save with content:', content?.substring(0, 200));

        // Call onChange directly to ensure image is saved
        if (content && content !== '<p><br></p>') {
          onChange(normalizeSpaces(content));
        }

        pendingImageInsert.current = false;

      } catch (error) {
        console.error('Error uploading image:', error);
        // Remove placeholder and show error
        quill.deleteText(range.index, 18);
        quill.insertText(range.index, '[Image upload failed]', { color: 'red' });
        alert('Failed to upload image. Please try again.');
        pendingImageInsert.current = false;
      }
    };
  }, [imageFolder, onChange]);

  // Create modules configuration based on whether image upload is enabled
  const modules = useMemo(() => {
    const baseToolbar = [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
    ];

    if (enableImageUpload) {
      baseToolbar.push(['image']);
    }

    baseToolbar.push(['clean']);

    const handlers = { link: linkHandler };
    if (enableImageUpload) {
      handlers.image = imageHandler;
    }

    const config = {
      toolbar: {
        container: baseToolbar,
        handlers
      },
      clipboard: {
        matchVisual: false
      }
    };

    return config;
  }, [enableImageUpload, imageHandler, linkHandler]);

  // Formats based on image upload setting
  const formats = useMemo(() => {
    return enableImageUpload ? IMAGE_FORMATS : BASE_FORMATS;
  }, [enableImageUpload]);
  
  // Only update Quill when value changes from external source (not our own onChange)
  useEffect(() => {
    if (!isInternalChange.current && value !== lastValue.current) {
      lastValue.current = value;
    }
    isInternalChange.current = false;
  }, [value]);
  
  const handleChange = useCallback((content, delta, source) => {
    // Process user-initiated changes only
    // Note: insertEmbed for images uses 'api' source, but we handle that directly in imageHandler
    if (source !== 'user') {
      return;
    }

    console.log('📝 RichTextEditor onChange (user):', content?.substring(0, 100));

    // Mark that this change came from us
    isInternalChange.current = true;
    lastValue.current = content;

    // If content is just empty paragraph tags, treat as empty
    if (content === '<p><br></p>' || content === '<p></p>') {
      onChange('');
      return;
    }

    // Pass the raw content to allow natural spaces behavior
    // Normalization should happen on save, not during editing
    onChange(content);
  }, [onChange]);

  return (
    <div 
      ref={containerRef}
      className={`rich-text-editor quill-editor ${className || ''}`}
      style={{ position: 'relative' }}
    >
      {/* Image Style Toolbar */}
      {showImageToolbar && selectedImage && enableImageUpload && (
        <div 
          key={imageClassKey}
          className="image-style-toolbar"
          style={{
            position: 'fixed',
            top: toolbarPosition.top,
            left: toolbarPosition.left,
            zIndex: 99999,
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            border: '2px solid #3b82f6',
            minWidth: '300px'
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Row 1: Size presets + alignment + close */}
          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            {[25, 50, 75, 100].map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => applyCustomWidth(pct)}
                title={`${pct}% width`}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: imageWidth === pct ? '700' : '500',
                  background: imageWidth === pct ? '#dbeafe' : 'transparent',
                  color: imageWidth === pct ? '#2563eb' : '#374151',
                  lineHeight: 1
                }}
              >
                {pct}%
              </button>
            ))}

            <div style={{ borderLeft: '1px solid #e1e5e9', height: '20px', margin: '0 4px' }} />

            {/* Alignment */}
            <button
              type="button"
              onClick={() => applyImageClass('align', 'img-left')}
              title="Float Left"
              style={{
                padding: '5px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                background: getImageClasses(selectedImage).align === 'img-left' ? '#dbeafe' : 'transparent',
                color: getImageClasses(selectedImage).align === 'img-left' ? '#2563eb' : '#374151'
              }}
            >
              <AlignLeft size={15} />
            </button>
            <button
              type="button"
              onClick={() => applyImageClass('align', 'img-center')}
              title="Center"
              style={{
                padding: '5px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                background: getImageClasses(selectedImage).align === 'img-center' ? '#dbeafe' : 'transparent',
                color: getImageClasses(selectedImage).align === 'img-center' ? '#2563eb' : '#374151'
              }}
            >
              <AlignCenter size={15} />
            </button>
            <button
              type="button"
              onClick={() => applyImageClass('align', 'img-right')}
              title="Float Right"
              style={{
                padding: '5px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                background: getImageClasses(selectedImage).align === 'img-right' ? '#dbeafe' : 'transparent',
                color: getImageClasses(selectedImage).align === 'img-right' ? '#2563eb' : '#374151'
              }}
            >
              <AlignRight size={15} />
            </button>

            <div style={{ flex: 1 }} />

            {/* Close */}
            <button
              type="button"
              onClick={() => { setShowImageToolbar(false); setSelectedImage(null); }}
              title="Close"
              style={{
                padding: '5px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                background: 'transparent',
                color: '#9ca3af'
              }}
            >
              <X size={15} />
            </button>
          </div>

          {/* Row 2: Width slider + number input */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="range"
              min="10"
              max="100"
              step="1"
              value={imageWidth}
              onChange={(e) => applyCustomWidth(parseInt(e.target.value))}
              className="image-width-slider"
              style={{ flex: 1, margin: 0, cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
              <input
                type="number"
                min="10"
                max="100"
                value={imageWidth}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val)) applyCustomWidth(val);
                }}
                style={{
                  width: '44px',
                  textAlign: 'center',
                  padding: '2px 4px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#374151'
                }}
              />
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>%</span>
            </div>
          </div>
        </div>
      )}

      {/* Resize handles overlay on selected image */}
      {showImageToolbar && selectedImage && enableImageUpload && !isDragging && (() => {
        const overlay = getOverlayRect();
        if (!overlay) return null;
        return (
          <div
            className="image-resize-overlay"
            style={{
              position: 'fixed',
              top: overlay.top,
              left: overlay.left,
              width: overlay.width,
              height: overlay.height,
              pointerEvents: 'none',
              border: '2px solid #3b82f6',
              borderRadius: '4px',
              zIndex: 99998
            }}
          >
            {/* Right edge handle */}
            <div
              className="resize-handle resize-handle-right"
              style={{
                position: 'absolute',
                right: -6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 8,
                height: 32,
                backgroundColor: '#3b82f6',
                borderRadius: 4,
                cursor: 'ew-resize',
                pointerEvents: 'all'
              }}
              onMouseDown={handleResizeStart}
            />
            {/* Bottom-right corner handle */}
            <div
              className="resize-handle resize-handle-corner"
              style={{
                position: 'absolute',
                right: -6,
                bottom: -6,
                width: 12,
                height: 12,
                backgroundColor: '#3b82f6',
                borderRadius: 3,
                cursor: 'nwse-resize',
                pointerEvents: 'all'
              }}
              onMouseDown={handleResizeStart}
            />
          </div>
        );
      })()}

      {/* HTML Source View */}
      {showHtmlSource ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 10px',
            background: '#1e293b',
            borderRadius: '8px 8px 0 0',
            borderBottom: '1px solid #334155'
          }}>
            <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, fontFamily: 'monospace' }}>
              HTML Source
            </span>
            <button
              type="button"
              onClick={toggleHtmlSource}
              style={{
                padding: '3px 10px',
                fontSize: '12px',
                fontWeight: 600,
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Apply & Close
            </button>
          </div>
          <textarea
            value={htmlSourceValue}
            onChange={(e) => setHtmlSourceValue(e.target.value)}
            style={{
              width: '100%',
              minHeight: minHeight,
              padding: '12px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '13px',
              lineHeight: '1.6',
              border: '1px solid #e1e5e9',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              resize: 'vertical',
              color: '#e2e8f0',
              background: '#0f172a',
              outline: 'none',
              tabSize: 2
            }}
            spellCheck={false}
          />
        </div>
      ) : (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{ minHeight }}
        />
      )}

      {/* HTML Source Toggle Button (always visible below toolbar) */}
      <button
        type="button"
        onClick={toggleHtmlSource}
        title={showHtmlSource ? 'Switch to Rich Text' : 'View HTML Source'}
        className="html-source-toggle"
        style={{
          position: 'absolute',
          top: '5px',
          right: '8px',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px 8px',
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: 'monospace',
          color: showHtmlSource ? '#3b82f6' : '#6b7280',
          background: showHtmlSource ? '#dbeafe' : 'transparent',
          border: showHtmlSource ? '1px solid #93c5fd' : '1px solid transparent',
          borderRadius: '4px',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        <Code size={13} />
        {showHtmlSource ? 'Rich Text' : '</>'}
      </button>

      {/* Link Modal */}
      {showLinkModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000
          }}
          onClick={() => setShowLinkModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              width: '420px',
              maxWidth: '90vw',
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              {linkSavedRange?.length > 0 || linkSelectedText ? 'Edit Link' : 'Insert Link'}
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                Display Text
              </label>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="e.g. Click here to apply"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <span style={{ fontSize: '11px', color: '#9ca3af' }}>Leave blank to show the URL as text</span>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                URL
              </label>
              <input
                ref={linkUrlInputRef}
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {linkSavedRange?.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setLinkUrl('');
                    applyLink();
                  }}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#dc2626',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    marginRight: 'auto'
                  }}
                >
                  Remove Link
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                style={{
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#374151',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyLink}
                style={{
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'white',
                  background: '#3b82f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {linkSavedRange?.length > 0 ? 'Update Link' : 'Insert Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;